// Based on https://ui.shadcn.com/docs/components/sidebar but converted to emotion components & simplified
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "@emotion/styled";

// Stable id for the mobile nav panel so the trigger button can reference it via aria-controls.
const MOBILE_NAV_PANEL_ID = "mobile-nav-panel";

type SidebarKeys = "verticalNav"; // Unique key to identify the sidebar instance
type SidebarKeyboardShortcuts = "\\" | undefined; // ctrl or win + key specified. If undefined, no shortcut is set.
type SidebarVariant = "sidebar" | "floating" | "inset";

type SidebarContextProps = {
  sidebarKey: SidebarKeys;
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: (open: boolean) => void;
  isMobile: boolean;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  toggleSidebar: () => void;
  keyboardShortcut: SidebarKeyboardShortcuts;
};

const SidebarContext = createContext<SidebarContextProps | null>(null);

function useSidebar() {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }
  return context;
}

/**
 * Like `useSidebar`, but returns null outside a provider instead of throwing. For presentational
 * pieces that render both inside the sidebar and standalone (nav helpers, and their unit tests),
 * where "no sidebar" is a legitimate state rather than a bug.
 */
function useOptionalSidebar(): SidebarContextProps | null {
  return useContext(SidebarContext);
}

type SidebarProviderProps = {
  sidebarKey: SidebarKeys; // Unique key to identify the sidebar instance
  children: React.ReactNode;

  // Controlled open state, required. The open/pinned policy — which tiers collapse, storage,
  // what pinning means — is product logic owned by the nav layer (see useSideNavOpenState).
  // This primitive only reflects it; there is no uncontrolled fallback.
  open: boolean;
  onOpenChange: (open: boolean | ((open: boolean) => boolean)) => void;

  keyboardShortcut?: SidebarKeyboardShortcuts; // ctrl or win + key; omit for none

  isMobile?: boolean; // When true, renders children in the mobile disclosure panel instead of the fixed column
};

function SidebarProvider({
  sidebarKey,
  open,
  onOpenChange: setOpen,
  keyboardShortcut,
  isMobile = false,
  children,
}: SidebarProviderProps) {
  const [openMobile, setOpenMobile] = useState(false);

  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setOpenMobile((prev) => !prev);
      return;
    }
    setOpen((prev) => !prev);
  }, [isMobile, setOpen]);

  // Close the mobile overlay when leaving the mobile tier, so resizing away and back doesn't
  // snap a stale panel open (and doesn't keep the Escape listener attached off-mobile).
  useEffect(() => {
    setOpenMobile(false);
  }, [isMobile]);

  // Escape closes the panel. Bail if a dropdown (or other dismissable layer) inside the panel
  // already handled the key — Radix calls preventDefault when it dismisses — so its Escape
  // doesn't also collapse the whole panel.
  useEffect(() => {
    if (!openMobile) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      setOpenMobile(false);
      // Contents unmount on close, so return focus to the trigger rather than dropping a
      // keyboard user onto <body>.
      document.querySelector<HTMLElement>(`[aria-controls="${MOBILE_NAV_PANEL_ID}"]`)?.focus();
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [openMobile]);

  // Adds a keyboard shortcut to toggle the sidebar.
  useEffect(() => {
    if (!keyboardShortcut) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Doing meta or ctrl so we don't need to detect if browser is macOS or not
      // as shown modifier is ctrl or on MacOS Command ⌘
      if (event.key === keyboardShortcut && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [keyboardShortcut, toggleSidebar]);

  const state = open ? "expanded" : "collapsed";

  const contextValue = useMemo<SidebarContextProps>(
    () => ({
      sidebarKey,
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
      keyboardShortcut,
    }),
    [sidebarKey, state, open, setOpen, isMobile, openMobile, toggleSidebar, keyboardShortcut],
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <SidebarProviderWrapper data-mobile={isMobile}>{children}</SidebarProviderWrapper>
    </SidebarContext.Provider>
  );
}

const SidebarProviderWrapper = styled.div(({ theme }) => ({
  display: "flex",
  minHeight: "100svh",
  width: "100%",
  "&:has(> [data-variant=inset])": {
    backgroundColor: theme.colors.sysBackgroundAlternative,
  },
  "&[data-mobile=true]": {
    flexDirection: "column",
  },
}));

type SidebarProps = {
  side: "left"; // Which side of the screen the sidebar should be on
  variant?: SidebarVariant; // How should the sidebar look
  collapsible?: "offcanvas" | "icon"; // When collapsed should the sidebar go offscreen or show just icons
  iconHover?: "overlay"; // When the sidebar is hovered and collapsed in icon mode what happens.
  "aria-label": string;
  children: React.ReactNode;
};

function Sidebar({
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  iconHover,
  "aria-label": ariaLabel,
  children,
}: SidebarProps) {
  const { state, isMobile, openMobile, setOpenMobile } = useSidebar();

  // This could be replaced with a CSS only solution but decided against it since the classnames get messy
  // e.g. [`${SidebarWrapper}[data-collapsible=icon]:not([data-icon-hover=overlay]) &, ${SidebarWrapper}[data-collapsible=icon][data-icon-hover=overlay]:not(:has(${SidebarRailInternal}:hover)):hover &`]:
  // which is much longer than [`${SidebarWrapper}[data-collapsible=icon] &`]
  // I did find the CSS solution worked better with region entering but JS worked better with leaving.
  // You can't really mix and match so picked JS since leaving is more important for user experience + classnames are cleaner.
  const [isHovering, setIsHovering] = useState(false);
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    return () => {
      if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    };
  }, []);

  if (isMobile) {
    // Panel stays mounted while closed (just `hidden`) so the trigger's `aria-controls` always
    // resolves, per the W3C disclosure pattern; `children` render only when open so the nav tree
    // isn't kept alive behind it.
    return (
      <MobileNavPanel
        id={MOBILE_NAV_PANEL_ID}
        aria-label={ariaLabel}
        data-mobile-nav="true"
        hidden={!openMobile}
        // A tap on a nav link dismisses the panel — including destinations that share the current
        // pathname (a pinned saved view, or the page you're already on), which a route-change
        // signal never sees. Guarded to links so dropdown triggers (buttons) still open their menus.
        onClick={(event) => {
          if (event.target instanceof HTMLElement && event.target.closest("a")) {
            setOpenMobile(false);
          }
        }}
      >
        {openMobile && children}
      </MobileNavPanel>
    );
  }

  return (
    <SidebarWrapper
      aria-label={ariaLabel}
      data-state={state}
      data-collapsible={state === "collapsed" ? (isHovering ? "" : collapsible) : ""}
      data-collapsible-ignore-hover={state === "collapsed" ? collapsible : ""}
      data-icon-hover={iconHover}
      data-variant={variant}
      data-side={side}
      onTouchStart={() => {
        if (iconHover !== undefined) setIsHovering(true);
      }}
      onMouseEnter={() => {
        if (iconHover !== undefined) {
          if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
          hoverTimeout.current = setTimeout(() => setIsHovering(true), 300);
        }
      }}
      onMouseLeave={() => {
        if (iconHover !== undefined) {
          if (hoverTimeout.current) {
            clearTimeout(hoverTimeout.current);
            hoverTimeout.current = null;
          }
          setIsHovering(false);
        }
      }}
    >
      <SidebarGap />
      <SidebarContainer>
        <SidebarInner>{children}</SidebarInner>
      </SidebarContainer>
    </SidebarWrapper>
  );
}

// These should not be used directly. Instead use the CSS variables defined in SidebarWrapper
const SIDEBAR_WIDTH = "300px";
const SIDEBAR_WIDTH_ICON = "64px";

/**
 * Used to create conditional styles based on the sidebar state. Do not use directly.
 */
const SidebarWrapper = styled.div({
  "--sidebar-width": SIDEBAR_WIDTH,
  "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
  "@media print": {
    display: "none",
  },
});

const SidebarGap = styled.div({
  position: "relative",
  width: "var(--sidebar-width)",
  backgroundColor: "transparent",
  transition: "width 200ms ease-in-out",

  [`${SidebarWrapper}[data-collapsible=offcanvas] &`]: {
    width: 0,
  },
  [`${SidebarWrapper}[data-variant=floating][data-collapsible-ignore-hover=icon] &, ${SidebarWrapper}[data-variant=inset][data-collapsible-ignore-hover=icon] &`]:
    {
      width: "calc(var(--sidebar-width-icon) + 1rem)",
    },
  [`${SidebarWrapper}[data-variant=sidebar][data-collapsible-ignore-hover=icon] &`]: {
    width: "var(--sidebar-width-icon)",
  },
});

const SidebarContainer = styled.div(({ theme }) => ({
  display: "flex",
  position: "fixed",
  insetBlock: 0,
  zIndex: 10,
  height: "100svh",
  width: "var(--sidebar-width)",
  transition: "left 200ms ease-in-out, right 200ms ease-in-out, width 200ms ease-in-out",

  left: 0,
  [`${SidebarWrapper}[data-collapsible=offcanvas] &`]: {
    left: "calc(var(--sidebar-width) * -1)",
  },
  [`${SidebarWrapper}[data-variant=floating] &, ${SidebarWrapper}[data-variant=inset] &`]: {
    padding: "8px",
  },
  [`${SidebarWrapper}[data-variant=floating][data-collapsible=icon] &, ${SidebarWrapper}[data-variant=inset][data-collapsible=icon] &`]:
    {
      width: "calc(var(--sidebar-width-icon) + 1rem + 2px)",
    },
  [`${SidebarWrapper}[data-variant=sidebar] &`]: {
    borderRight: `1px solid ${theme.colors.sysBorderDecorative}`,
    boxSizing: "content-box", // So border doesn't affect width
  },
  [`${SidebarWrapper}[data-variant=sidebar][data-collapsible=icon] &`]: {
    width: "var(--sidebar-width-icon)",
  },
  [`${SidebarWrapper}[data-variant=sidebar][data-icon-hover=overlay] &`]: {
    transition:
      "left 200ms ease-in-out, right 200ms ease-in-out, width 200ms ease-in-out, filter 200ms ease-in-out",
  },
  [`${SidebarWrapper}[data-variant=sidebar][data-icon-hover=overlay][data-collapsible-ignore-hover=icon][data-collapsible=""] &`]:
    {
      filter: "drop-shadow(rgba(0, 0, 0, 0.1) 0px 4px 10px)",
    },
}));

const SidebarInner = styled.nav(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.colors.sysBackgroundDefault,
  height: "100%",
  width: "100%",

  [`${SidebarWrapper}[data-variant=floating] &`]: {
    border: `1px solid ${theme.colors.sysBorderDecorative}`,
    borderRadius: "10px",
    boxShadow: "0 1px 3px 0 #0000001a, 0 1px 2px -1px #0000001a",
    backgroundColor: theme.colors.sysBackgroundAlternative,
  },
  [`${SidebarWrapper}[data-variant=inset] &`]: {
    backgroundColor: theme.colors.sysBackgroundAlternative,
  },
}));

///// CORE ABOVE THIS LINE /////

// The mobile nav menu: a plain disclosure panel (not a modal) below the top bar, so the bar stays
// interactive. `--sidebar-width: 100%` lets the reused Content/Footer width calc()s resolve.
const MobileNavPanel = styled.nav(({ theme }) => ({
  "--sidebar-width": "100%",
  // A normal flex child after the top bar, not position:fixed: fixed offsets measure from the
  // viewport, so anything above the bar (AdminBar, AnnouncementCard) would misalign it.
  flex: 1,
  minHeight: 0, // lets SidebarContent's overflow:auto scroll instead of stretching
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  backgroundColor: theme.colors.sysBackgroundDefault,
  // `display: flex` above is an author style, so it beats the UA `[hidden] { display: none }`
  // rule — without this override the closed panel stays laid out and steals the page's height.
  "&[hidden]": {
    display: "none",
  },
}));

/**
 * Wrap the main **content** area when using a sidebar in this (not the Sidebar).
 */
const SidebarInset = styled.main(({ theme }) => ({
  width: "100%",
  backgroundColor: theme.colors.sysBackgroundDefault,
  position: "relative",
  overflow: "auto",
  "@media screen and (min-width: 1200px)": {
    [`${SidebarWrapper}[data-variant=inset] + &`]: {
      margin: "8px",
      marginLeft: 0,
      borderRadius: "14px",
      boxShadow: "0 1px 3px 0 #0000001a, 0 1px 2px -1px #0000001a",
    },
    [`${SidebarWrapper}[data-variant=inset][data-state=collapsed] + &`]: {
      marginLeft: "8px",
    },
  },
  "@media print": {
    overflow: "visible",
  },
}));

const SidebarHeader = styled.div({
  display: "flex",
  flexDirection: "column",
  padding: "12px 8px 8px 8px",
  gap: "8px",
});

const SidebarContent = styled.div({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minHeight: 0,
  overflow: "auto",
  gap: "8px",

  [`${SidebarWrapper}[data-collapsible=icon] &`]: {
    overflow: "hidden",
  },
});

const SidebarFooter = styled.div({
  display: "flex",
  flexDirection: "column",
  padding: "8px",
  gap: "8px",
});

const SidebarGroup = styled.div({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  minWidth: 0,
  padding: "8px 16px",
  position: "relative",
  gap: "4px",
});

const SidebarMenu = styled.ul({
  all: "unset",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  minWidth: 0,
  gap: "4px",
  overflow: "hidden",
});

/**
 * Provides a clickable border to toggle the sidebar open and closed.
 */
function SidebarRail(props: React.ComponentProps<"button">) {
  const { toggleSidebar } = useSidebar();
  return (
    <SidebarRailInternal
      aria-label="Toggle sidebar"
      aria-hidden={true} // Screen readers should use button instead of this shortcut
      tabIndex={-1}
      onClick={toggleSidebar}
      title="Toggle sidebar"
      {...props}
    />
  );
}

const SidebarRailInternal = styled.button(({ theme }) => ({
  position: "absolute",
  insetBlock: 0,
  zIndex: 20,
  width: "16px",
  right: "-16px",
  translate: "calc(calc(1/2*100%)*-1) 0",
  backgroundColor: "transparent",
  border: "none",
  cursor: "w-resize",
  transition: "background-color 150ms ease-in-out",

  "@media (max-width: 576px)": {
    display: "none",
  },

  [`${SidebarWrapper}[data-state=collapsed] &`]: {
    cursor: "e-resize",
  },
  [`${SidebarWrapper}[data-collapsible=offcanvas] &`]: {
    right: "-8px",
    translate: "0 0",
    "::after": {
      left: "100%",
    },
    "&:hover": {
      backgroundColor: theme.colors.sysBackgroundAlternative,
    },
  },

  "::after": {
    content: '""',
    position: "absolute",
    insetBlock: 0,
    left: "50%",
    width: "2px",
  },
  "&:hover": {
    "::after": {
      backgroundColor: theme.colors.sysBorderDecorative,
    },
  },
}));

export {
  Sidebar,
  SidebarProvider,
  useSidebar,
  useOptionalSidebar,
  SidebarInset,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarRail,
  SidebarWrapper,
  SidebarGroup,
  SidebarMenu,
  MOBILE_NAV_PANEL_ID,
};
